import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { deploymentService, type CreateDeploymentDto } from '@/services/deployment/deployment.service';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, ArrowLeft, Save } from 'lucide-react';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';

export default function CreateDeploymentPage() {
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const [formData, setFormData] = useState<CreateDeploymentDto>({
        subscriptionId: '',
        ipAddress: '',
        frontendDomain: '',
        backendDomain: '',
        filesDomain: '',
    });

    const { data: subscriptions, isLoading, error } = useQuery({
        queryKey: ['deployments', 'available-subscriptions'],
        queryFn: () => deploymentService.getAvailableSubscriptions(),
    });

    const createMutation = useMutation({
        mutationFn: (data: CreateDeploymentDto) => deploymentService.create(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['deployments'] });
            toast.success('Deployment created successfully');
            navigate('/admin/deployments');
        },
        onError: (error: any) => {
            toast.error(error?.message || 'Failed to create deployment');
        },
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.subscriptionId) {
            toast.error('Please select a subscription');
            return;
        }
        createMutation.mutate(formData);
    };

    if (isLoading) {
        return (
            <div className="space-y-6">
                <Skeleton className="h-8 w-64" />
                <Skeleton className="h-[400px] w-full" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="space-y-6">
                <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back
                </Button>
                <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                        Failed to load available subscriptions. Please try again later.
                    </AlertDescription>
                </Alert>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center gap-4">
                <Button variant="ghost" onClick={() => navigate(-1)} size="sm">
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back
                </Button>
                <div>
                    <h1 className="text-2xl font-semibold">Create Deployment</h1>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                        Set up a new deployment for an active subscription
                    </p>
                </div>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle>Deployment Details</CardTitle>
                    <CardDescription>
                        Enter the deployment configuration for the selected subscription
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="space-y-2">
                            <Label htmlFor="subscription">Subscription *</Label>
                            <Select
                                value={formData.subscriptionId}
                                onValueChange={(value) =>
                                    setFormData((prev) => ({ ...prev, subscriptionId: value }))
                                }
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Select a subscription" />
                                </SelectTrigger>
                                <SelectContent>
                                    {subscriptions?.map((sub) => (
                                        <SelectItem key={sub.id} value={sub.id}>
                                            {sub.company?.name} - {sub.user?.email} ({sub.plan?.displayName})
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {(!subscriptions || subscriptions.length === 0) && (
                                <p className="text-sm text-slate-500">
                                    No active subscriptions without deployments available.
                                </p>
                            )}
                        </div>

                        <div className="grid md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="ipAddress">IP Address *</Label>
                                <Input
                                    id="ipAddress"
                                    placeholder="192.168.1.1"
                                    value={formData.ipAddress}
                                    onChange={(e) =>
                                        setFormData((prev) => ({ ...prev, ipAddress: e.target.value }))
                                    }
                                    required
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="frontendDomain">Frontend Domain *</Label>
                                <Input
                                    id="frontendDomain"
                                    placeholder="app.example.com"
                                    value={formData.frontendDomain}
                                    onChange={(e) =>
                                        setFormData((prev) => ({ ...prev, frontendDomain: e.target.value }))
                                    }
                                    required
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="backendDomain">Backend Domain *</Label>
                                <Input
                                    id="backendDomain"
                                    placeholder="api.example.com"
                                    value={formData.backendDomain}
                                    onChange={(e) =>
                                        setFormData((prev) => ({ ...prev, backendDomain: e.target.value }))
                                    }
                                    required
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="filesDomain">Files Domain (Optional)</Label>
                                <Input
                                    id="filesDomain"
                                    placeholder="files.example.com"
                                    value={formData.filesDomain || ''}
                                    onChange={(e) =>
                                        setFormData((prev) => ({ ...prev, filesDomain: e.target.value }))
                                    }
                                />
                            </div>
                        </div>

                        <div className="flex justify-end gap-4">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => navigate('/admin/deployments')}
                            >
                                Cancel
                            </Button>
                            <Button type="submit" disabled={createMutation.isPending}>
                                <Save className="mr-2 h-4 w-4" />
                                {createMutation.isPending ? 'Creating...' : 'Create Deployment'}
                            </Button>
                        </div>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
}
